import classNames from 'classnames';
import { AnimatePresence, m as motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import Image from '@/shared/components/shared/Image';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { ImageShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';

export type DepthSliderSlide = {
    id: string;
    label: string;
    category: string;
    title: string;
    description: string;
    image: {
        src: string;
        alt?: string;
        width: number;
        height: number;
    };
    backgroundColor?: string;
    blob1Color?: string;
    blob2Color?: string;
};

export type DepthSliderSectionProps = {
    background: ImageShape;
    slides: DepthSliderSlide[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    depthSliderSectionProps: DepthSliderSectionProps;
};

const TEXT_TRANSITION = {
    duration: 0.55,
    ease: [0.25, 0.25, 0, 1],
};

const PLANE_GAP = 5;
const CAMERA_Z_OFFSET = 0.75;
const CAMERA_INTRO_RAW_END = 0.1;
const CAMERA_INTRO_Z_OFFSET = 1.5;
const PLANE_ASPECT_RATIO = 580 / 722;
const PLANE_X_SIDES = [-1, 1, -1, 1, -1, 1, -1];
const PLANE_X_OFFSET_FACTORS = [0.9, 0.8, 0.7, 1, 0.7, 0.8, 0.9];
const PARALLAX_X = 0.15;
const PARALLAX_Y = 0.08;
const PARALLAX_SMOOTHING = 0.08;
const FADE_SMOOTHING = 0.12;
const CAMERA_SMOOTHING = 0.08;

const TRAIL_POINTS = 64;
const TRAIL_LEAD_OFFSET = 1.6;
const TRAIL_START_X = -0.5;
const TRAIL_END_X = 0;
const TRAIL_WIDTH = 0.4;
const TRAIL_CYCLES_X = 3;
const TRAIL_START_Y = -0.15;
const TRAIL_END_Y = -0.35;
const TRAIL_AMPLITUDE_Y = 0.12;
const TRAIL_CYCLES_Y = 4;
const TRAIL_COLOR = '#f6f9ff';
const TRAIL_OPACITY = 0.55;
const TRAIL_STRAIGHT_FROM = 0.9;
const TRAIL_FADE_IN_TO = 0.08;
const TRAIL_FADE_OUT_FROM = 0.92;
const MOBILE_BREAKPOINT = 768;
const MOBILE_NAV_MEDIA = '(max-width: 767px), (max-width: 900px) and (orientation: landscape)';
const FIRST_SLIDE_HOLD_VH = 0.75;

const BORDER_RADIUS_DESKTOP = 18;
const BORDER_RADIUS_COMPACT = 12;

const getScrollHoldRatio = (slidesCount: number) =>
    slidesCount <= 1 ? 0 : FIRST_SLIDE_HOLD_VH / (slidesCount + FIRST_SLIDE_HOLD_VH - 1);

const mapScrollProgress = (rawProgress: number, slidesCount: number) => {
    if (slidesCount <= 1) return 0;

    const holdRatio = getScrollHoldRatio(slidesCount);
    if (rawProgress <= holdRatio) return 0;

    return (rawProgress - holdRatio) / (1 - holdRatio);
};

const getRawProgressForSlide = (index: number, slidesCount: number): number => {
    if (slidesCount <= 1) return 0;

    const holdRatio = getScrollHoldRatio(slidesCount);
    if (index <= 0) return holdRatio * 0.5;

    return holdRatio + (index / (slidesCount - 1)) * (1 - holdRatio);
};

const mapCameraZ = (rawProgress: number, sliderProgress: number, maxCameraZ: number, minCameraZ: number): number => {
    if (rawProgress < CAMERA_INTRO_RAW_END) {
        const introCameraZ = maxCameraZ + CAMERA_INTRO_Z_OFFSET;
        const t = THREE.MathUtils.smoothstep(rawProgress / CAMERA_INTRO_RAW_END, 0, 1);
        return THREE.MathUtils.lerp(introCameraZ, maxCameraZ, t);
    }

    return THREE.MathUtils.lerp(maxCameraZ, minCameraZ, sliderProgress);
};

const getEdgeInsetPercent = (screenWidth: number): number => {
    const offsetX = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--offset-x'));

    if (Number.isFinite(offsetX) && offsetX > 0) {
        return offsetX / screenWidth;
    }

    if (screenWidth <= 768) return 0.05;
    if (screenWidth <= 1024) return 0.045;
    if (screenWidth <= 1440) return 0.04;

    return 0.035;
};

const getPlaneCenterWorldX = (
    screenWidth: number,
    visibleWidth: number,
    planeWidthWorld: number,
    side: number,
    offsetFactor: number,
): number => {
    const minInset = getEdgeInsetPercent(screenWidth);
    const halfPlane = planeWidthWorld / visibleWidth / 2;
    const maxShift = Math.max(0, 0.5 - minInset - halfPlane * 2);
    const inset = minInset + (1 - offsetFactor) * maxShift * 0.5;
    const centerFraction = side < 0 ? inset + halfPlane : 1 - inset - halfPlane;

    return (centerFraction - 0.5) * visibleWidth;
};

const DepthSliderSection = ({ depthSliderSectionProps, ...props }: Props) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const slides = depthSliderSectionProps.slides;
    const { background } = depthSliderSectionProps;
    const activeSlide = slides[activeIndex];

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const scrollProgressRef = useRef(0);
    const pointerRef = useRef({ x: 0, y: 0 });
    const activeIndexRef = useRef(0);

    const navListRef = useRef<HTMLUListElement>(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });

    useMotionValueEvent(scrollYProgress, 'change', (latest) => {
        scrollProgressRef.current = latest;
    });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const maxCameraZ = PLANE_GAP / 2 + CAMERA_Z_OFFSET;
        const introCameraZ = maxCameraZ + CAMERA_INTRO_Z_OFFSET;
        const lastPlaneZ = -(slides.length - 1) * PLANE_GAP;
        const minCameraZ = lastPlaneZ + maxCameraZ;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100);
        camera.position.set(0, 0, introCameraZ);

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.setClearColor(0x000000, 0);

        const textureLoader = new THREE.TextureLoader();
        const planeGeometry = new THREE.PlaneGeometry(3, 3);
        const planes: THREE.Mesh[] = [];

        const getPlaneAspectRatio = (screenWidth: number): number => {
            if (screenWidth <= 768) return 159 / 168;
            if (screenWidth <= 1024) return 264 / 330;
            return PLANE_ASPECT_RATIO;
        };

        let currentPlaneAspectRatio = getPlaneAspectRatio(canvas.clientWidth || window.innerWidth);

        const getBorderRadius = (screenWidth: number): number =>
            screenWidth <= 1024 ? BORDER_RADIUS_COMPACT : BORDER_RADIUS_DESKTOP;

        const createRoundedRectAlphaTexture = (width: number, height: number, radius: number): THREE.CanvasTexture => {
            const dpr = Math.min(window.devicePixelRatio, 2);
            const canvasEl = document.createElement('canvas');
            canvasEl.width = Math.max(1, Math.round(width * dpr));
            canvasEl.height = Math.max(1, Math.round(height * dpr));
            const ctx = canvasEl.getContext('2d');
            if (ctx) {
                ctx.scale(dpr, dpr);
                const r = Math.min(radius, width / 2, height / 2);
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.roundRect(0, 0, width, height, r);
                ctx.fill();
            }
            const texture = new THREE.CanvasTexture(canvasEl);
            texture.needsUpdate = true;
            return texture;
        };

        let currentMaskTexture: THREE.CanvasTexture | null = null;

        const applyTextureCrop = (mesh: THREE.Mesh, aspectRatio: number) => {
            const material = mesh.material as THREE.MeshBasicMaterial;
            const texture = material.map;
            if (!texture) return;

            const imageAr = mesh.userData.imageAr as number;
            if (imageAr > aspectRatio) {
                const rx = aspectRatio / imageAr;
                texture.repeat.set(rx, 1);
                texture.offset.set((1 - rx) / 2, 0);
            } else {
                const ry = imageAr / aspectRatio;
                texture.repeat.set(1, ry);
                texture.offset.set(0, (1 - ry) / 2);
            }
        };

        slides.forEach((slide, index) => {
            const side = PLANE_X_SIDES[index % PLANE_X_SIDES.length] ?? -1;
            const offsetFactor = PLANE_X_OFFSET_FACTORS[index % PLANE_X_OFFSET_FACTORS.length] ?? 1;
            const material = new THREE.MeshBasicMaterial({
                transparent: true,
                depthWrite: false,
                opacity: index === 0 ? 1 : 0,
                color: '#ffffff',
            });
            const mesh = new THREE.Mesh(planeGeometry, material);
            mesh.position.set(0, 0, -index * PLANE_GAP);
            mesh.userData.side = side;
            mesh.userData.offsetFactor = offsetFactor;
            mesh.userData.baseX = 0;
            mesh.userData.imageAr = slide.image.width / slide.image.height;
            scene.add(mesh);
            planes.push(mesh);

            textureLoader
                .loadAsync(slide.image.src)
                .then((texture) => {
                    texture.colorSpace = THREE.SRGBColorSpace;
                    material.map = texture;
                    material.needsUpdate = true;
                    applyTextureCrop(mesh, currentPlaneAspectRatio);
                })
                .catch(() => undefined);
        });

        const trailPositions = new Float32Array(TRAIL_POINTS * 3);
        const trailGeometry = new THREE.BufferGeometry();
        const trailPositionAttribute = new THREE.BufferAttribute(trailPositions, 3);
        trailGeometry.setAttribute('position', trailPositionAttribute);
        const trailMaterial = new THREE.LineBasicMaterial({
            color: TRAIL_COLOR,
            transparent: true,
            opacity: 0,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
        });
        const trailLine = new THREE.Line(trailGeometry, trailMaterial);
        trailLine.frustumCulled = false;
        scene.add(trailLine);

        const trailEndZ = minCameraZ - TRAIL_LEAD_OFFSET;
        const trailRange = maxCameraZ - trailEndZ;

        let currentCameraZ = introCameraZ;
        let pointerCurrent = { x: 0, y: 0 };
        let rafId = 0;
        let isMobileViewport = false;

        const getDesiredWidthFraction = (screenWidth: number): number => {
            if (screenWidth <= 768) return (180 / screenWidth) * 1.4;
            if (screenWidth <= 1024) return 264 / screenWidth;
            if (screenWidth <= 1280) return Math.min(330 / screenWidth, 0.3);
            if (screenWidth <= 1440) return Math.min(440 / screenWidth, 0.35);
            return Math.min(580 / screenWidth, 0.4);
        };

        const getCardScale = (desiredWidthFraction: number, visibleWidth: number, aspectRatio: number): number =>
            (desiredWidthFraction * visibleWidth) / (3 * aspectRatio);

        const resize = () => {
            const w = canvas.clientWidth || window.innerWidth;
            const h = canvas.clientHeight || window.innerHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h, false);

            const fovRad = (camera.fov * Math.PI) / 180;
            const visibleHeight = 2 * maxCameraZ * Math.tan(fovRad / 2);
            const visibleWidth = visibleHeight * camera.aspect;
            currentPlaneAspectRatio = getPlaneAspectRatio(w);
            isMobileViewport = w <= MOBILE_BREAKPOINT;
            const desiredWidthFraction = getDesiredWidthFraction(w);
            const baseScale = getCardScale(desiredWidthFraction, visibleWidth, currentPlaneAspectRatio);
            const planeWidthWorld = 3 * baseScale * currentPlaneAspectRatio;

            const widthPx = desiredWidthFraction * w;
            const heightPx = widthPx / currentPlaneAspectRatio;
            const maskTexture = createRoundedRectAlphaTexture(widthPx, heightPx, getBorderRadius(w));
            currentMaskTexture?.dispose();
            currentMaskTexture = maskTexture;

            planes.forEach((plane) => {
                plane.scale.set(baseScale * currentPlaneAspectRatio, baseScale, 1);
                plane.userData.baseX = getPlaneCenterWorldX(
                    w,
                    visibleWidth,
                    planeWidthWorld,
                    plane.userData.side as number,
                    plane.userData.offsetFactor as number,
                );
                applyTextureCrop(plane, currentPlaneAspectRatio);

                const material = plane.material as THREE.MeshBasicMaterial;
                material.alphaMap = maskTexture;
                material.needsUpdate = true;
            });
        };
        resize();
        window.addEventListener('resize', resize);

        const animate = () => {
            rafId = requestAnimationFrame(animate);

            const rawProgress = scrollProgressRef.current;
            const progress = mapScrollProgress(rawProgress, slides.length);
            const targetCameraZ = mapCameraZ(rawProgress, progress, maxCameraZ, minCameraZ);
            currentCameraZ = THREE.MathUtils.lerp(currentCameraZ, targetCameraZ, CAMERA_SMOOTHING);
            camera.position.z = Math.max(currentCameraZ, minCameraZ);

            pointerCurrent.x = THREE.MathUtils.lerp(pointerCurrent.x, pointerRef.current.x, PARALLAX_SMOOTHING);
            pointerCurrent.y = THREE.MathUtils.lerp(pointerCurrent.y, pointerRef.current.y, PARALLAX_SMOOTHING);

            const normalizedDepth = THREE.MathUtils.clamp(
                (maxCameraZ - camera.position.z) / PLANE_GAP,
                0,
                slides.length - 1,
            );
            const curr = Math.floor(normalizedDepth);
            const next = Math.min(curr + 1, slides.length - 1);
            const blend = normalizedDepth - curr;

            const activeIdx = blend >= 0.5 ? next : curr;
            if (activeIdx !== activeIndexRef.current) {
                activeIndexRef.current = activeIdx;
                setActiveIndex(activeIdx);
            }

            planes.forEach((plane, i) => {
                let targetOpacity = 0;
                if (i === curr) targetOpacity = 1 - blend;
                if (i === next) targetOpacity = Math.max(targetOpacity, blend);
                const mat = plane.material as THREE.MeshBasicMaterial;
                mat.opacity = THREE.MathUtils.lerp(mat.opacity, targetOpacity, FADE_SMOOTHING);

                const opacity = mat.opacity;
                plane.position.x = (plane.userData.baseX as number) + pointerCurrent.x * PARALLAX_X * opacity;
                plane.position.y = pointerCurrent.y * PARALLAX_Y * opacity;
            });

            const headZ = Math.max(camera.position.z - TRAIL_LEAD_OFFSET, trailEndZ);
            for (let i = 0; i < TRAIL_POINTS; i += 1) {
                const frac = i / (TRAIL_POINTS - 1);
                const z = THREE.MathUtils.lerp(maxCameraZ, headZ, frac);
                const u = (maxCameraZ - z) / trailRange;
                const uEase = THREE.MathUtils.smoothstep(u, 0, TRAIL_STRAIGHT_FROM);
                const decay = 1 - uEase;
                trailPositions[i * 3] =
                    THREE.MathUtils.lerp(TRAIL_START_X, TRAIL_END_X, uEase) +
                    Math.sin(u * Math.PI * 2 * TRAIL_CYCLES_X) * TRAIL_WIDTH * decay;
                trailPositions[i * 3 + 1] =
                    THREE.MathUtils.lerp(TRAIL_START_Y, TRAIL_END_Y, uEase) +
                    Math.sin(u * Math.PI * 2 * TRAIL_CYCLES_Y) * TRAIL_AMPLITUDE_Y * decay;
                trailPositions[i * 3 + 2] = z;
            }
            trailPositionAttribute.needsUpdate = true;

            const trailFadeIn = THREE.MathUtils.smoothstep(progress, 0, TRAIL_FADE_IN_TO);
            const trailFadeOut = isMobileViewport
                ? 1 - THREE.MathUtils.smoothstep(progress, TRAIL_FADE_OUT_FROM, 1)
                : 1;
            const trailTargetOpacity = trailFadeIn * trailFadeOut * TRAIL_OPACITY;
            trailMaterial.opacity = THREE.MathUtils.lerp(trailMaterial.opacity, trailTargetOpacity, FADE_SMOOTHING);

            renderer.render(scene, camera);
        };
        animate();

        const handlePointerMove = (e: PointerEvent) => {
            pointerRef.current = {
                x: (e.clientX / window.innerWidth) * 2 - 1,
                y: -((e.clientY / window.innerHeight) * 2 - 1),
            };
        };
        const handlePointerLeave = () => {
            pointerRef.current = { x: 0, y: 0 };
        };

        canvas.addEventListener('pointermove', handlePointerMove);
        canvas.addEventListener('pointerleave', handlePointerLeave);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', resize);
            canvas.removeEventListener('pointermove', handlePointerMove);
            canvas.removeEventListener('pointerleave', handlePointerLeave);
            planes.forEach((plane) => {
                const material = plane.material as THREE.MeshBasicMaterial;
                material.map?.dispose();
                material.dispose();
            });
            renderer.dispose();
            planeGeometry.dispose();
            trailGeometry.dispose();
            trailMaterial.dispose();
            currentMaskTexture?.dispose();
        };
    }, [slides]);

    const handleNavClick = (index: number) => {
        if (!containerRef.current || slides.length <= 1) return;

        const rect = containerRef.current.getBoundingClientRect();
        const containerTop = window.scrollY + rect.top;
        const scrollRange = containerRef.current.offsetHeight - window.innerHeight;
        const targetOffset = getRawProgressForSlide(index, slides.length) * scrollRange;

        window.scrollTo({ top: containerTop + targetOffset, behavior: 'smooth' });
    };

    useEffect(() => {
        const list = navListRef.current;
        if (!list || !window.matchMedia(MOBILE_NAV_MEDIA).matches) return;

        const activeBtn = list.querySelector<HTMLElement>('.depth-slider-section__nav-btn--active');
        if (!activeBtn) return;

        const listRect = list.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();
        const delta = btnRect.left + btnRect.width / 2 - (listRect.left + listRect.width / 2);

        list.scrollTo({
            left: list.scrollLeft + delta,
            behavior: 'smooth',
        });
    }, [activeIndex]);

    const isImageOnRight = (PLANE_X_SIDES[activeIndex % PLANE_X_SIDES.length] ?? -1) > 0;
    const [isTextOnLeft, setIsTextOnLeft] = useState(isImageOnRight);
    const isImageOnRightRef = useRef(isImageOnRight);
    isImageOnRightRef.current = isImageOnRight;
    const handleTextExitComplete = () => setIsTextOnLeft(isImageOnRightRef.current);

    const tpCategory = useTypograf(activeSlide.category);
    const tpTitle = useTypograf(activeSlide.title);
    const tpDescription = useTypograf(activeSlide.description);

    return (
        <div
            ref={containerRef}
            className="depth-slider-section-container"
            style={{ height: `${(slides.length + FIRST_SLIDE_HOLD_VH) * 100}vh` }}
        >
            <section
                {...props}
                className={classNames('depth-slider-section js-header-theme-trigger', props.className)}
                data-theme="dark"
            >
                <div className="depth-slider-section__background" aria-hidden="true">
                    <Image
                        src={background.src}
                        alt={background.alt ?? ''}
                        width={background.width ?? 1920}
                        height={background.height ?? 1080}
                        sizes="100vw"
                        priority={background.priority ?? true}
                    />
                </div>

                <canvas ref={canvasRef} className="depth-slider-section__canvas" />

                <div className="depth-slider-section__wrapper wrapper">
                    <div
                        className={classNames('depth-slider-section__info', {
                            'depth-slider-section__info--left': isTextOnLeft,
                        })}
                    >
                        <AnimatePresence mode="wait" onExitComplete={handleTextExitComplete}>
                            <motion.div
                                key={activeSlide.id}
                                className="depth-slider-section__info-top"
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -24 }}
                                transition={TEXT_TRANSITION}
                            >
                                {activeSlide.category && (
                                    <p className="depth-slider-section__category text-l">{tpCategory}</p>
                                )}
                                {activeSlide.title && (
                                    <h2
                                        className="depth-slider-section__title h2"
                                        dangerouslySetInnerHTML={{ __html: tpTitle }}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>

                        <AnimatePresence mode="wait">
                            {activeSlide.description && (
                                <motion.div
                                    key={activeSlide.id}
                                    className="depth-slider-section__description text-m"
                                    initial={{ opacity: 0, y: 40 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -24 }}
                                    transition={TEXT_TRANSITION}
                                    dangerouslySetInnerHTML={{ __html: tpDescription }}
                                />
                            )}
                        </AnimatePresence>
                    </div>

                    <nav className="depth-slider-section__nav" aria-label="Слайды">
                        <ul ref={navListRef} className="depth-slider-section__nav-list list-unstyled">
                            {slides.map((slide, index) => (
                                <li key={slide.id} className="depth-slider-section__nav-item">
                                    {slide.label && (
                                        <button
                                            type="button"
                                            className={classNames('depth-slider-section__nav-btn text-xs', {
                                                'depth-slider-section__nav-btn--active': index === activeIndex,
                                            })}
                                            onClick={() => handleNavClick(index)}
                                            aria-current={index === activeIndex ? 'true' : undefined}
                                        >
                                            {tp(slide.label)}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            </section>
        </div>
    );
};

export default DepthSliderSection;
